import {
  FluidAnalyticsInterface,
  FluidButtonType,
  FluidDataLayerComponentConfiguration,
  FluidModalAction,
  FluidSize,
  FluidTheme,
  FluidThemeInterface,
  generateUniqueId,
} from '@lmig/fluid-core';
import { FluidModalTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-element-translations.type';
import { FluidKeyboardIntercept } from '@lmig/fluid-core/lib/structs/interfaces/fluid-keyboard-intercept.interface';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  MODAL_CLOSE_PREVENTED,
  MODAL_PRIMARY_ACTIONS,
  MODAL_SECONDARY_ACTIONS,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ESCAPE, EventUtils } from '../../utils/events/event-utils';
import { tabbable } from '../../utils/focus-trap/tabbable';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import {
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalOverlay,
} from './components/fluid-modal.components';

/**
 * @displayName Modal
 *
 * @contextData FLUID Modal is a dialog box that displays content in a layer above the rest of the page. Modals are meant
 * to interrupt the user's workflow and focus their attention on a particular task or piece of information. While open,
 * the user cannot interact with the underlying page until the modal has been dismissed.
 * <br/><br/>
 * Modals should be used sparingly for short or infrequent tasks such as editing or management tasks. If a task must be
 * repeatedly performed, it should be done from the main page.
 *
 * @slot - Content displayed within the Modal
 * @slot action-set - Named slot for Modal action buttons. Only available when "modalActions" Prop is NOT used
 *
 * @analyticsAware ['actionClicked', 'modalClosed']
 */
@Component({
  tag: 'fluid-modal',
  styleUrl: 'fluid-modal.less',
  shadow: true,
})
export class FluidModal
  implements ComponentInterface, FluidThemeInterface, FluidAnalyticsInterface
{
  // ================================================================ //
  // -- Own Properties (private)

  /**
   * Internal reference to MutationObserver that monitors Modal's
   * "title" attr for changes. Used to remove MutationObserver from
   * memory on "disconnectedCallback()".
   */
  private _mutationObserver: MutationObserver;

  /**
   * When false, indicates that Modal is nested within an <iframe>.
   */
  private _childOfTopWindow = false;

  /**
   * Reference to the main, top-level root "document". Used for nested
   * <iframe> Modal extraction & to disable background scrolling.
   */
  private _topDocument: Document;

  /**
   * Reference to Modal's next element sibling within its <iframe>
   * origin. Only used if Modal is nested within an <iframe>.
   */
  private _originNextSiblingRef: HTMLElement;

  /**
   * Reference to Modal's parent element within its <iframe> origin.
   * Only used if Modal is nested within an <iframe>.
   */
  private _originParentRef: HTMLElement;

  /**
   * Reference to "target" element within the top-level document that
   * <iframe> Modal will be appended to when it's opened. Only used
   * if Modal is nested within an <iframe>.
   */
  private _targetContainerRef: HTMLElement;

  /**
   * Reference to Modal's embedded Focus Trap. Only used if Modal is
   * nested within an <iframe>.
   */
  private _focusTrapRef: HTMLFluidFocusTrapElement;

  /**
   * Reference to the Modal body container.
   */
  private _containerRef: HTMLElement;

  /**
   * Reference to Modal "dismiss" Button.
   */
  private _dismissBtnRef: HTMLElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidModalElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidModalTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * When true, sets Modal container to "display: none;". Set to false
   * when Modal is open, then set to true AFTER Modal fade-out animation
   * completes. Used to prevent FOUC when Modal is first rendered. Also
   * prevents any slotted Modal children with tabbable Shadow DOM elements
   * from receiving :focus when Modal is closed.
   */
  @State() _disableInteraction = true;

  // ================================================================ //
  // -- Props
  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Text to be displayed in Modal header title.
   */
  @Prop({ mutable: true }) modalTitle: string;

  /**
   * Sets the size of the Modal container.
   */
  @Prop({ mutable: true }) size: FluidSize = FluidSize.MEDIUM;

  /**
   * Controls whether Modal is visible (true) or hidden (false).
   */
  @Prop({ mutable: true }) open = false;

  @Watch('open')
  onOpenChanged() {
    if (this.open) {
      this._disableInteraction = false;
      this.setAutoFocusNode();
    }
    this.setWindowLocation();
    this.toggleScrollDisabled();
  }

  /**
   * When true, Modal's backdrop overlay will be transparent instead
   * of translucent.
   */
  @Prop({ mutable: true }) transparentOverlay = false;

  /**
   * When true, clicking anywhere outside the Modal body container
   * (i.e. anywhere on the backdrop overlay) will close it. NOTE: If
   * your Modal requires users to complete an action/task before close,
   * you should set this Prop to false.
   */
  @Prop({ mutable: true }) outsideClickClose = true;

  /**
   * Populates the Modal's action buttons. If this Prop is provided &
   * "outsideClickClose" is set to false, the Modal will be considered
   * an "action Modal" and will validate that 1 primary action exists
   * and at least 1 secondary action exists. Each action button emits an
   * "actionClicked" event containing its given "eventKey" when clicked.
   * If this Prop is NOT provided, Modal will render an "action-set"
   * named Slot.
   */
  @Prop({ mutable: true }) modalActions: FluidModalAction[];

  /**
   * When true, all action buttons will be disabled EXCEPT for those
   * with "enableOnModalDisabled" property set to true. NOTE: If you need
   * a Modal to complete an action before closing, set this to true while
   * that action completes.
   */
  @Prop({ mutable: true }) actionsDisabled: boolean;

  /**
   * Disables Modal's "close" button IF this Prop is true AND
   * "actionsDisabled" Prop is true as well (i.e. both Props must be true
   * for "close" button to be disabled).
   * @featureAdded 20th April, 2021 (Opt-In Only)
   */
  @Prop({ mutable: true }) shouldDisableClose = false;

  /**
   * When true, removes Modal's "close" button. NOTE: This should be used
   * with caution - if you haven't set up a "close" action button with
   * corresponding event listener OR enabled "outsideClickClose", you
   * won't be able to close the Modal!
   */
  @Prop({ mutable: true }) hideCloseIcon = false;

  /**
   * When TRUE, limits Modal's max-height to 70% & allows internal
   * scrolling within the Modal's body, but also truncates any content
   * that overflows the edges of the Modal container (such as Popovers).
   * When FALSE, Modal's height will be dictated by the height of its
   * content, and content WILL be allowed to display outside the Modal
   * container edges without truncation (see usage examples).
   */
  @Prop({ mutable: true }) limitOverflow = false;

  /**
   * Whether to disable background page scrolling while Modal is open.
   * If false, page behind Modal will be allowed to scroll. NOTE: this
   * Prop SHOULD be enabled if possible for best user experience.
   */
  @Prop({ mutable: true }) disableBackgroundScroll: boolean;

  /**
   * By default, Modal will "trap" :focus when open (i.e. Modal will
   * prevent :focus from escaping its children via tab navigation).
   * This Prop will disable "focus trap" functionality when true.
   */
  @Prop({ mutable: true }) disableFocusTrap: boolean;

  /**
   * When true, Modal will automatically transfer :focus to the first
   * child element that can receive :focus when opened. NOTE: Modal
   * must have at least one slotted child element that can receive
   * :focus for this Prop to function correctly.
   */
  @Prop({ mutable: true }) autoFocus: boolean;

  /**
   * When true, prevents Modal from promoting itself within the DOM
   * to the top document level in an attempt to escape an <iframe>.
   * This default behavior is not desireable in some scenarios and
   * causes rendering issues.
   */
  @Prop({ mutable: true }) disableTopDocumentIFrameShift: boolean;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when an action button is clicked. Contains the
   * action's "eventKey" property (FluidModalAction).
   */
  @Event() actionClicked: EventEmitter<string>;

  /**
   * Event emitted when Modal is closed.
   */
  @Event() modalClosed: EventEmitter<boolean>;

  /**
   * Event emitted when Modal is closed via outside click.
   */
  @Event() outsideClick: EventEmitter;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the modal in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   * @action: getModalTitle - sets "modalTitle" Prop to "title" attribute value
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    useI18n.bind(this)();
    this.getModalTitle();
    this._mutationObserver = Utils.watchAttr('title', this.host, () =>
      this.getModalTitle(true)
    );
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: getWindowLocation - determines if Modal is nested within <iframe>
   * @action: validateActions - ensures primary/secondary actions are provided
   */
  componentWillLoad(): void {
    this._disableInteraction = !this.open;
    this.getWindowLocation();
    this.validateActions();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: setFocusTrapRoot - ensures Focus Trap is configured properly
   * @action: setAutoFocusNode - if "autoFocus" is true, set child to receive :focus
   * @action: setWindowLocation - relocates <iframe> Modal to top-level document
   * @action: toggleScrollDisabled - disables background scrolling if Modal is open
   * @action: setupAnalytics - FLUID Data Layer configuration setup
   */
  componentDidLoad() {
    this.setFocusTrapRoot();
    this.setAutoFocusNode();
    this.setWindowLocation();
    this.toggleScrollDisabled();
    this.setupAnalytics();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: enableBodyScroll - re-enable scrolling if component is removed from DOM
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    Utils.enableBodyScroll(this._topDocument);

    delete this._topDocument;
    delete this._originNextSiblingRef;
    delete this._originParentRef;
    delete this._targetContainerRef;
    delete this._focusTrapRef;
    delete this._containerRef;
    delete this._dismissBtnRef;

    this._mutationObserver?.disconnect();
    delete this._mutationObserver;

    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  @Prop({ mutable: true }) keyboardIntercept: FluidKeyboardIntercept<
    any,
    boolean
  > = {};

  /**
   * Closes Modal when "escape" key pressed UNLESS "outsideClickClose" is
   * disabled AND "close" button is disabled/hidden.
   */
  @Listen('keydown')
  onKeyDown(e: KeyboardEvent) {
    switch (true) {
      case !EventUtils.isKey(e, ESCAPE):
      case !this.outsideClickClose && this.hideCloseIcon:
      case !this.outsideClickClose &&
        this.actionsDisabled &&
        this.shouldDisableClose:
        return;
      default:
        this.handleEscapeToClose();
    }
  }

  /**
   * Checks if a keyboardIntercept object has been configured, and if so, calls its function before subscribing
   * to the return promise, and finally closing the modal.
   */
  handleEscapeToClose() {
    this.keyboardIntercept.escape
      ? this.keyboardIntercept.escape().then((allowClose: boolean) => {
          allowClose
            ? this.closeModal()
            : Utils.consoleWarn(this.host, MODAL_CLOSE_PREVENTED);
        })
      : this.closeModal();
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Opens or closes the Modal. If parameter is not provided, sets "open"
   * Prop to the inverse of its current value.
   * @param open - whether Modal should be open (true) or closed (false)
   */
  @Method() async toggleModal(open = !this.open) {
    open ? (this.open = true) : this.closeModal();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * If "title" attribute is provided instead of "modalTitle" Prop, sets
   * "modalTitle" to "title" attribute's value.
   */
  getModalTitle(update = false): void {
    if ((!update && !!this.modalTitle) || !this.host.title) return;
    this.modalTitle = this.host.title;
  }

  /**
   * When "autoFocus" is true, sets child element that receives :focus
   * when Modal opens. This is necessary because of the "dismiss" button -
   * it's technically the first tabbable element within the Modal body, so
   * it would receive :focus by default instead of the first slotted child.
   * This method configures Focus Trap to transfer :focus to the first
   * SLOTTED child element.
   */
  setAutoFocusNode(): void {
    if (!this.autoFocus || !this._dismissBtnRef) return;

    const dismissBtn = Utils.querySelector(this._dismissBtnRef, 'button');
    const autoFocusNode = tabbable(this._containerRef, {
      includeContainer: false,
      includeHidden: true, // override "hidden element" filter since Modal is closed
      filterFn: (el: HTMLElement) => el !== dismissBtn, // filter out "dismiss" button from tabbables
    })[0];

    !!autoFocusNode && this._focusTrapRef.setAutoFocusNode(autoFocusNode);
  }

  /**
   * Sets Focus Trap's "rootElement" @Prop to Modal's main container
   * element if it wasn't already set during 1st render.
   */
  setFocusTrapRoot(): void {
    if (this._focusTrapRef?.rootElement) return;
    this._focusTrapRef.rootElement = this._containerRef;
  }

  /**
   * Determines if Modal is nested within an <iframe> - if so, gets
   * references to <iframe> siblings (for re-insertion) & top-level
   * document container (where Modal will be moved to). Short-circuits
   * if cross-origin frame error is thrown.
   */
  getWindowLocation(): void {
    try {
      this._childOfTopWindow = window === window.top;
      this._topDocument = window.top.document;

      if (!this._childOfTopWindow) {
        this._originNextSiblingRef = this.host
          .nextElementSibling as HTMLElement;
        this._originParentRef = this.host.parentElement;
        this._targetContainerRef = this._topDocument.body;
      }
    } catch (e) {
      // Probably a cross-origin frame error, so disable Modal extraction
      this._topDocument = window.document;
      this._childOfTopWindow = true;
    }
  }

  /**
   * Ensures Modal appears in the top-level document so that its overlay
   * fills the entire screen & background scrolling is properly disabled.
   * When Modal is open, extracts Modal from <iframe> & appends it to
   * the top-level parent container. When Modal is closed, re-inserts
   * it back into the <iframe> in its exact original DOM position.
   * NOTE: Only runs if Modal implementation supports Shadow DOM.
   */
  setWindowLocation(): void {
    if (
      this._childOfTopWindow ||
      !this.host.shadowRoot ||
      !this._targetContainerRef ||
      this.disableTopDocumentIFrameShift
    ) {
      return;
    }

    if (this.open && this.host.ownerDocument !== this._topDocument) {
      this._targetContainerRef.appendChild(this.host);
      this._focusTrapRef?.setOwnerDocument();
    } else if (!this.open && this.host.ownerDocument === this._topDocument) {
      this._originNextSiblingRef
        ? this._originNextSiblingRef.before(this.host)
        : this._originParentRef.appendChild(this.host);
      this._focusTrapRef?.setOwnerDocument();
    }
  }

  /**
   * Validates that we have at most one primary action, and at least
   * one secondary. NB: This will not block rendering, rather, it will
   * show a console warning so the dev can see :)
   */
  validateActions() {
    if (this.outsideClickClose) return;

    let primaryActions, secondaryActions;
    if (!this.modalActions) {
      const actions: HTMLFluidButtonElement[] = Utils.getChildrenOfType(
        this.host,
        'fluid-button'
      );
      primaryActions = actions.filter(
        (b) => b.type === FluidButtonType.PRIMARY
      );
      secondaryActions = actions.filter(
        (b) => b.type === FluidButtonType.SECONDARY
      );
    } else {
      primaryActions = this.modalActions.filter(
        (b) => b.type === FluidButtonType.PRIMARY
      );
      secondaryActions = this.modalActions.filter(
        (b) => b.type === FluidButtonType.SECONDARY
      );
    }

    if (primaryActions.length === 0 || primaryActions.length > 1) {
      Utils.consoleWarn(this.host, MODAL_PRIMARY_ACTIONS);
    }
    if (secondaryActions.length === 0) {
      Utils.consoleWarn(this.host, MODAL_SECONDARY_ACTIONS);
    }
  }

  /**
   * If "disableBackgroundScroll" is true & Modal is open, disables
   * background page scrolling.
   */
  toggleScrollDisabled(): void {
    if (this.open && this.disableBackgroundScroll) {
      Utils.disableBodyScroll(this._topDocument);
    } else {
      Utils.enableBodyScroll(this._topDocument);
    }
  }

  /**
   * Closes the Modal if "outsideClickClose" is true & Modal is open.
   * Triggered when click occurs anywhere outside the Modal body container.
   */
  handleOutsideClick(): void {
    if (!this.outsideClickClose) return;
    this.outsideClick.emit();
    this.closeModal();
  }

  /**
   * Closes the Modal when "dismiss" button is clicked & "actionsDisabled"
   * & "shouldDisableClose" Props are not both true simultaneously.
   */
  handleDismissButtonClick(): void {
    if (this.actionsDisabled && this.shouldDisableClose) return;
    this.closeModal();
  }

  /**
   * Emits "actionClicked" Event containing the action's "eventKey" when
   * action button is clicked. Also registers event in data layer.
   * @param action - FluidModalAction object of the action button that was clicked
   */
  handleActionClick(action: FluidModalAction): void {
    this.actionClicked.emit(action.eventKey);
    sendEvent(this.host, 'actionClicked', action);
  }

  /**
   * Sets Modal's root element to "display: none" when Modal is closed
   * (AFTER "fade out" animation ends). This ensures Modal's tabbable child
   * elements do NOT erroneously receive :focus when Modal is closed.
   */
  disableInteraction() {
    if (this.open) return;
    this._disableInteraction = true;
  }

  /**
   * Closes the Modal & emits events to let the calling component
   * know that the Modal has been closed.
   */
  closeModal() {
    this.open = false;
    this.modalClosed.emit(true);
    sendEvent(this.host, this.parentLayerId, 'modalClosed');
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        onAnimationEnd={() => this.disableInteraction()}
        style={{ 'z-index': `${this.zIndex}` }}
        aria-hidden={String(!this.open)}
        class={{
          [`fluid-modal ${this.theme}`]: true,
          'modal-visible': this.open,
          'modal-hidden': !this.open,
          'disable-interaction': this._disableInteraction,
        }}
        data-test-id={this.identifier}>
        {/* Modal Overlay */}
        <ModalOverlay
          identifier={this.identifier}
          handleOutsideClick={() => this.handleOutsideClick()}
          transparentOverlay={this.transparentOverlay}
        />

        {/* Focus Trap */}
        <fluid-focus-trap
          enabled={!this.disableFocusTrap && this.open}
          rootElement={this._containerRef}
          autoFocus={this.autoFocus}
          disableAutoActivation={this.disableFocusTrap}
          ref={(el) => (this._focusTrapRef = el)}
          class={{ 'size-full': this.size === FluidSize.FULL }}
          data-test-id={`${this.identifier}-modal-focus`}>
          {/* Modal Container */}
          <ModalContainer
            identifier={this.identifier}
            modalTitle={
              this._i18nStore?.translate(this.fluidTranslate?.modalTitle) ||
              this.modalTitle
            }
            size={this.size}
            limitOverflow={this.limitOverflow}
            setContainerRef={(el: HTMLDivElement) => (this._containerRef = el)}>
            {/* Modal Header */}
            <ModalHeader
              identifier={this.identifier}
              modalTitle={this.modalTitle}
              theme={this.theme}
              actionsDisabled={this.actionsDisabled}
              autoFocus={this.autoFocus}
              handleDismissButtonClick={() => this.handleDismissButtonClick()}
              hideCloseIcon={this.hideCloseIcon}
              setDismissBtnRef={(el: HTMLFluidButtonElement) =>
                (this._dismissBtnRef = el)
              }
              fluidTranslate={this.fluidTranslate}
              shouldDisableClose={this.shouldDisableClose}
              i18nStore={this._i18nStore}
            />

            {/* Modal Body */}
            <ModalBody
              identifier={this.identifier}
              modalActions={this.modalActions}
              handleActionClick={(action: FluidModalAction) =>
                this.handleActionClick(action)
              }
              actionsDisabled={this.actionsDisabled}
              theme={this.theme}
              limitOverflow={this.limitOverflow}
              actionSetSlot={this.host.querySelector('[slot="action-set"]')}
            />
          </ModalContainer>
        </fluid-focus-trap>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for Modal container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(this.host)?.id;
  }

  // ================================================================ //
  // -- Deprecated Methods

  /**
   * DEPRECATED: "toggleModalOpen" is now deprecated. Instead, use the
   * "toggleModal" Prop (same functionality, just a different naming
   * convention) -- Opens or closes the Modal. If parameter is not
   * provided, sets "open" Prop to the inverse of its current value.
   * @param open - whether Modal should be open (true) or closed (false)
   * @deprecated
   */
  @Method() async toggleModalOpen(open = !this.open) {
    return this.toggleModal(open);
  }
}
